import { createContext, useContext, useState, useEffect, useCallback } from "react";
import type { ReactNode } from "react";
import { useAuth } from "./AuthContext";
import { getUserStats, getUsageStats, updateDietaryPreferences } from "./userStats";
import type { UserStats, DietType, MonthlyUsage } from "./userStats";
import type { DietaryPreferences } from "./gemini";

interface UserStatsContextType {
  stats: UserStats | null;
  loading: boolean;
  error: string | null;
  dietaryPrefs: DietaryPreferences | undefined;
  monthlyUsage: MonthlyUsage | null;
  refreshStats: () => Promise<void>;
  updatePrefs: (dietType: DietType, allergies: string[]) => Promise<void>;
}

const UserStatsContext = createContext<UserStatsContextType | undefined>(undefined);

export function UserStatsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [monthlyUsage, setMonthlyUsage] = useState<MonthlyUsage | null>(null);

  const dietaryPrefs: DietaryPreferences | undefined =
    stats && (stats.dietType || stats.allergies?.length)
      ? { dietType: stats.dietType || "none", allergies: stats.allergies || [] }
      : undefined;

  const loadStats = useCallback(async () => {
    if (!user) {
      setStats(null);
      setMonthlyUsage(null);
      setLoading(false);
      return;
    }
    try {
      const [data, usage] = await Promise.all([
        getUserStats(user.uid),
        getUsageStats(user.uid),
      ]);
      setStats(data);
      setMonthlyUsage(usage);
      setError(null);
    } catch (err) {
      console.error("Failed to load user stats:", err);
      setError("Unable to load your stats. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    setLoading(true);
    loadStats();
  }, [loadStats]);

  const refreshStats = useCallback(async () => {
    if (!user) return;
    try {
      const [data, usage] = await Promise.all([
        getUserStats(user.uid),
        getUsageStats(user.uid),
      ]);
      setStats(data);
      setMonthlyUsage(usage);
      setError(null);
    } catch (err) {
      console.error("Failed to refresh stats:", err);
    }
  }, [user]);

  const updatePrefs = useCallback(async (dietType: DietType, allergies: string[]) => {
    if (!user) return;
    await updateDietaryPreferences(user.uid, dietType, allergies);
    setStats((prev) => prev ? { ...prev, dietType, allergies } : null);
  }, [user]);

  return (
    <UserStatsContext.Provider value={{ stats, loading, error, dietaryPrefs, monthlyUsage, refreshStats, updatePrefs }}>
      {children}
    </UserStatsContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useUserStats() {
  const context = useContext(UserStatsContext);
  if (!context) throw new Error("useUserStats must be used within UserStatsProvider");
  return context;
}
